import { useRef, useState } from "react";
import { LINK_COLOURS, linkAdjacent, linkPathProblem, type PublicPlay } from "@unfaithful/shared";

type LinkView = NonNullable<PublicPlay["linkUp"]>;

/**
 * Link-up in your hand: the whole courtyard, with your colours drawable.
 * Press on one of your lanterns (or your path) and drag square by square.
 * The path is sent when you lift your finger.
 */
export function LinkPad({ lu, meId, onPath }: { lu: LinkView; meId: string; onPath: (colour: number, cells: number[]) => void }) {
  const n = lu.size;
  const mine = lu.owners.map((o, i) => (o.includes(meId) ? i : -1)).filter((i) => i >= 0);
  const [colour, setColour] = useState(mine[0] ?? 0);
  const [draft, setDraft] = useState<number[] | null>(null);
  const svg = useRef<SVGSVGElement>(null);
  const others = new Set(lu.ends.flatMap((e, i) => (i === colour ? [] : e)));

  const cellAt = (e: React.PointerEvent): number | null => {
    const r = svg.current?.getBoundingClientRect();
    if (!r) return null;
    const x = Math.floor(((e.clientX - r.left) / r.width) * n);
    const y = Math.floor(((e.clientY - r.top) / r.height) * n);
    return x < 0 || y < 0 || x >= n || y >= n ? null : y * n + x;
  };

  const start = (e: React.PointerEvent) => {
    const c = cellAt(e);
    if (c === null) return;
    // Pressing a lantern of another of your colours switches to it.
    const owned = mine.find((i) => lu.ends[i]!.includes(c));
    const col = owned ?? colour;
    if (!mine.includes(col)) return;
    const path = lu.paths[col] ?? [];
    let next: number[] | null = null;
    if (lu.ends[col]!.includes(c)) next = [c];
    else if (path.includes(c)) next = path.slice(0, path.indexOf(c) + 1);
    if (!next) return;
    setColour(col);
    setDraft(next);
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const move = (e: React.PointerEvent) => {
    if (!draft) return;
    const c = cellAt(e);
    if (c === null) return;
    const last = draft[draft.length - 1]!;
    if (c === last) return;
    if (draft.length > 1 && c === draft[draft.length - 2]) {
      setDraft(draft.slice(0, -1));
      return;
    }
    const ends = lu.ends[colour]!;
    const finished = draft.length > 1 && ends.includes(last);
    if (finished || !linkAdjacent(last, c, n) || draft.includes(c) || others.has(c)) return;
    setDraft([...draft, c]);
  };

  const end = () => {
    if (!draft) return;
    const cells = draft.length === 1 ? [] : draft;
    if (!linkPathProblem(cells, colour, lu.ends, n)) onPath(colour, cells);
    setDraft(null);
  };

  // While you drag, your draft replaces your colour's path and cuts across others.
  const taken = new Set(draft ?? []);
  const shown = lu.paths.map((p, i) => {
    if (draft && i === colour) return draft;
    if (!draft) return p;
    const cut = p.findIndex((c) => taken.has(c));
    return cut < 0 ? p : p.slice(0, cut);
  });
  const centre = (c: number) => `${(c % n) * 100 + 50},${Math.floor(c / n) * 100 + 50}`;

  return (
    <div className="lp">
      {mine.length > 1 && (
        <div className="lp-colours" role="group" aria-label="Your colours">
          {mine.map((i) => (
            <button
              key={i}
              className={`lp-colour ${i === colour ? "is-on" : ""}`}
              style={{ "--link": LINK_COLOURS[i]!.colour } as React.CSSProperties}
              onClick={() => setColour(i)}
            >
              {LINK_COLOURS[i]!.shape} {LINK_COLOURS[i]!.name}
            </button>
          ))}
        </div>
      )}
      <svg
        ref={svg}
        className="lp-board"
        viewBox={`0 0 ${n * 100} ${n * 100}`}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        role="img"
        aria-label="The courtyard. Drag from one of your lanterns to its twin."
      >
        {Array.from({ length: n * n }, (_, c) => (
          <rect key={c} className="lp-stone" x={(c % n) * 100 + 3} y={Math.floor(c / n) * 100 + 3} width={94} height={94} rx={8} />
        ))}
        {shown.map((p, i) =>
          p.length > 1 ? (
            <polyline
              key={`p${i}`}
              className={`lp-path ${mine.includes(i) ? "is-mine" : ""}`}
              points={p.map(centre).join(" ")}
              stroke={LINK_COLOURS[i]!.colour}
            />
          ) : null,
        )}
        {lu.ends.map((pair, i) =>
          pair.map((c, k) => (
            <g key={`e${i}-${k}`} className={`lp-end ${i === colour ? "is-active" : mine.includes(i) ? "is-mine" : ""}`}>
              <circle cx={(c % n) * 100 + 50} cy={Math.floor(c / n) * 100 + 50} r={36} fill={LINK_COLOURS[i]!.colour} />
              <text x={(c % n) * 100 + 50} y={Math.floor(c / n) * 100 + 64} textAnchor="middle">
                {LINK_COLOURS[i]!.shape}
              </text>
            </g>
          )),
        )}
      </svg>
    </div>
  );
}
